// acceptInvite.ts — the invite callback as a pure state machine (F7 §4.4).
//
// Everything the /auth/accept route DECIDES lives here, with no DOM and no
// Supabase import, so that every decision is provable under `node --test`
// without a browser. What the route DOES (call the auth client, sign out,
// render) lives in auth/AcceptInvite.tsx and is deliberately thin; the two
// effects this module needs — clearing the address bar and verifying the
// token — are handed to `beginAccept` by the component.
//
// THE FLOW (architect ruling Q3, 250926: option (b) refused; code exchange).
// The invite email links `/auth/accept?token_hash=…&type=invite` directly
// (supabase/templates/invite.html). No session credential is ever in a URL:
// the page reads the single-use token_hash, clears the address bar with
// replaceState BEFORE the network call, and exchanges the hash through
// `verifyOtp`. The implicit flow — a session in the URL fragment — is gone,
// not kept as a fallback: a fragment that carries a token is refused.
//
// One rule shapes every type below: NOTHING returned from this module
// carries a token. The token_hash is read inside `beginAccept`, handed to
// the verifier, and dropped; access_token and refresh_token are read only
// to decide whether they are present. A `Landing`, a `State` or an `Event`
// can therefore be held in React state, rendered, or (by a future mistake)
// logged without ever carrying a credential.

/** The one route production's Auth redirect allowlist names (§4.4). */
export const ACCEPT_PATH = "/auth/accept";

/**
 * How long the route waits for the exchange before declaring the link
 * dead. `verifyOtp` is one network round trip, normally sub-second; the
 * bound only stops a hung request from spinning forever. It is generous on
 * purpose: the token is single-use, and a false failure on a slow
 * connection costs LINNFOSS a fresh invitation, whereas a longer spinner
 * costs the user seconds.
 */
export const SESSION_WAIT_MS = 15_000;

/**
 * Password policy. Twelve characters, no composition rules: length is the
 * one property that reliably raises the cost of guessing, and composition
 * rules push people toward predictable substitutions (NIST SP 800-63B's
 * position, which this follows). The server enforces its own minimum as
 * well; this is the stricter of the two. Seventy-two BYTES is the upper
 * bound because GoTrue hashes with bcrypt, which silently ignores every
 * byte past the 72nd; a password that is longer than it can be is refused
 * here rather than quietly truncated there.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_BYTES = 72;

/** Why a landing or an exchange ended without a password being chosen. */
export type FailureReason =
  | "link_expired" // the Auth host said the token is expired or already used
  | "link_invalid" // the Auth host (or the URL) reported any other error
  | "token_missing" // no token_hash: opened by hand, reloaded, or Back/Forward
  | "type_unsupported" // a token for a flow this route does not serve
  | "fragment_refused" // a session in the URL fragment: the removed implicit flow
  | "no_session"; // the exchange did not produce a session within SESSION_WAIT_MS

/** The verifyOtp types this route serves. */
// magiclink (DESIGN_SLICE_INVITE_SCREEN 281026 V1.0): the invite screen's mail;
// its exchange signs the respondent in and continues without a password step.
// signup: the same mail's first sending to a NEW person. Supabase Auth sends the
// Confirm sign up template, not Magic Link, when signInWithOtp creates the
// identity (the invite screen's first press, 0024); supabase/templates/signup.html
// links here with type=signup, and the exchange continues exactly as magiclink.
export type OtpType = "invite" | "recovery" | "magiclink" | "signup";

export type Landing = { kind: "exchange"; otpType: OtpType } | { kind: "error"; reason: FailureReason };

/**
 * The sentence above the choose-password form. A recovery link reaches the
 * same form as an invitation, and must not tell the user their invitation
 * was confirmed.
 */
export function choosePasswordLead(landing: Landing): string {
  return landing.kind === "exchange" && landing.otpType === "recovery"
    ? "Your reset link is confirmed. Choose a new password for next time."
    : "Your invitation is confirmed. Choose a password for next time.";
}

/**
 * Classify the URL the browser arrived with, from its query and fragment.
 *
 * Precedence, first match wins:
 *   1. a fragment carrying access_token or refresh_token → fragment_refused.
 *      The implicit flow is removed; a link that still produces one (an
 *      unchanged `{{ .ConfirmationURL }}` template, a crafted URL) is a
 *      failure, never a session. The auth client does not consume it either
 *      (`detectSessionInUrl: false`, src/lib/supabase.ts).
 *   2. an Auth error in the query or the fragment → link_expired or
 *      link_invalid, through a fixed allowlist of codes.
 *   3. no token_hash, an empty one, or more than one → token_missing.
 *   4. `type` other than invite or recovery → type_unsupported.
 *
 * Which `type`s are served: `invite` is the route's purpose. `recovery` is
 * served too, and deliberately: a consultant who forgets their password
 * has no self-service reset in Release 1, and production's redirect
 * allowlist names exactly this one URL, so a platform-operated recovery
 * link can only land here — where "choose a password, then sign in fresh"
 * is precisely the flow it needs. Every other type is refused.
 *
 * The token values are never returned.
 */
export function classifyLanding(search: string, hash: string): Landing {
  const query = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const fragment = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);

  if (fragment.has("access_token") || fragment.has("refresh_token")) {
    return { kind: "error", reason: "fragment_refused" };
  }

  for (const params of [query, fragment]) {
    if (params.has("error") || params.has("error_code") || params.has("error_description")) {
      return { kind: "error", reason: failureFromErrorCode(params.get("error_code")) };
    }
  }

  const hashes = query.getAll("token_hash");
  if (hashes.length !== 1 || hashes[0] === "") return { kind: "error", reason: "token_missing" };

  const type = query.get("type");
  if (type === "invite" || type === "recovery" || type === "magiclink" || type === "signup") {
    return { kind: "exchange", otpType: type };
  }
  return { kind: "error", reason: "type_unsupported" };
}

/**
 * The fixed allowlist of Auth error codes this route distinguishes. Anything
 * not named here collapses to "link_invalid"; an error's description is
 * never consulted, so nothing the Auth host (or a crafted link) puts in the
 * URL or the response is ever echoed to the screen. GoTrue answers a
 * token_hash that is expired OR already used with `otp_expired`.
 */
function failureFromErrorCode(code: string | null | undefined): FailureReason {
  return code === "otp_expired" ? "link_expired" : "link_invalid";
}

/** What the component hands `beginAccept`: the landing URL and two effects. */
export interface AcceptDeps {
  search: string;
  hash: string;
  /** Replace the current history entry's URL with `scrubTarget` (replaceState). */
  strip: () => void;
  /** `supabase.auth.verifyOtp`, narrowed to the token-hash form. */
  verifyOtp: (params: {
    token_hash: string;
    type: OtpType;
  }) => Promise<{ data: { session: object | null }; error: { code?: string | null | undefined } | null }>;
}

/**
 * Start the exchange. Synchronously: classify the landing, then clear the
 * address bar — ALWAYS, whatever the landing was — and only then, for a
 * valid landing, call the verifier. The strip comes first, before the
 * network call and before the component's first render is committed, so
 * the single-use token is in the address bar for no longer than the
 * page's own script takes to start, and never in any history entry that
 * Back or Forward can reach: replaceState rewrites the landing entry in
 * place. A later visit to that entry is a plain `/auth/accept`, which is
 * token_missing — the exchange cannot run twice.
 *
 * `outcome` is null for a failed landing (the state machine fails it
 * directly), else the Event the exchange ends in. It never rejects.
 */
export function beginAccept(deps: AcceptDeps): { landing: Landing; outcome: Promise<Event> | null } {
  const landing = classifyLanding(deps.search, deps.hash);
  const tokenHash =
    landing.kind === "exchange" ? new URLSearchParams(deps.search).get("token_hash") ?? "" : "";
  deps.strip();
  if (landing.kind !== "exchange") return { landing, outcome: null };

  const outcome = deps.verifyOtp({ token_hash: tokenHash, type: landing.otpType }).then(
    ({ data, error }): Event => {
      if (error) return { type: "exchange_failed", reason: failureFromErrorCode(error.code) };
      if (!data.session) return { type: "exchange_failed", reason: "no_session" };
      return { type: "session" };
    },
    (): Event => ({ type: "exchange_failed", reason: "link_invalid" }),
  );
  return { landing, outcome };
}

export type State =
  | { step: "landing" }
  | { step: "awaiting_session"; otpType: OtpType }
  | { step: "choose_password"; problem: string | null }
  | { step: "updating" }
  | { step: "continue" }
  | { step: "failed"; reason: FailureReason };

export type Event =
  | { type: "landed"; landing: Landing }
  | { type: "session" } // verifyOtp returned a session
  | { type: "exchange_failed"; reason: FailureReason } // verifyOtp refused the token
  | { type: "timeout" } // SESSION_WAIT_MS elapsed without an outcome
  | { type: "submit"; password: string; confirm: string }
  | { type: "update_ok" }
  | { type: "update_failed"; code: string | null };

export const INITIAL_STATE: State = { step: "landing" };

/**
 * The transition function. Every (state, event) pair not listed returns the
 * state unchanged, so a late or duplicated event (a second outcome, a
 * timeout that fires after the session arrived) cannot move the route
 * backwards or out of a terminal state.
 *
 *   landing ──landed(exchange)─────────────▶ awaiting_session(otpType)
 *   landing ──landed(error)────────────────▶ failed
 *   awaiting_session ──session─────────────▶ choose_password      (invite, recovery)
 *   awaiting_session ──session─────────────▶ continue             (magiclink, signup: signed in by the click)
 *   awaiting_session ──exchange_failed─────▶ failed(reason)
 *   awaiting_session ──timeout─────────────▶ failed(no_session)
 *   choose_password ──submit(ok)───────────▶ updating
 *   choose_password ──submit(bad)──────────▶ choose_password(problem)
 *   updating ──update_ok───────────────────▶ continue             (the session is kept; "/" loads)
 *   updating ──update_failed───────────────▶ choose_password(problem)
 */
export function nextStep(state: State, event: Event): State {
  switch (state.step) {
    case "landing":
      if (event.type !== "landed") return state;
      if (event.landing.kind === "error") return { step: "failed", reason: event.landing.reason };
      return { step: "awaiting_session", otpType: event.landing.otpType };

    case "awaiting_session":
      if (event.type === "session") {
        return signsInByTheClick(state.otpType) ? { step: "continue" } : { step: "choose_password", problem: null };
      }
      if (event.type === "exchange_failed") return { step: "failed", reason: event.reason };
      if (event.type === "timeout") return { step: "failed", reason: "no_session" };
      return state;

    case "choose_password": {
      if (event.type !== "submit") return state;
      const problem = checkPassword(event.password, event.confirm);
      return problem === null ? { step: "updating" } : { step: "choose_password", problem };
    }

    case "updating":
      if (event.type === "update_ok") return { step: "continue" };
      if (event.type === "update_failed") {
        return { step: "choose_password", problem: messageForUpdateFailure(event.code) };
      }
      return state;

    case "continue":
    case "failed":
      return state;
  }
}

/** The mails whose click alone signs the person in: no password is asked for. */
export function signsInByTheClick(type: OtpType): boolean {
  return type === "magiclink" || type === "signup";
}

/** The password policy. Returns the problem to show, or null when it passes. */
export function checkPassword(password: string, confirm: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Choose a password of at least ${PASSWORD_MIN_LENGTH} characters.`;
  }
  if (new TextEncoder().encode(password).length > PASSWORD_MAX_BYTES) {
    return `Choose a password of at most ${PASSWORD_MAX_BYTES} bytes.`;
  }
  if (password !== confirm) {
    return "The two entries do not match.";
  }
  return null;
}

/**
 * updateUser failures, through a fixed allowlist of Auth error codes. The
 * server's own message is never shown: it is not needed for the two cases
 * a user can act on, and the discipline of "nothing from the Auth host is
 * echoed" is easier to keep as a rule than as a judgement.
 */
export function messageForUpdateFailure(code: string | null): string {
  switch (code) {
    case "weak_password":
      return "That password is too easy to guess. Choose a longer or less common one.";
    case "same_password":
      return "Choose a password you have not used for this account before.";
    default:
      return "The password could not be set. Try again, or ask LINNFOSS for a new invitation.";
  }
}

/**
 * The fixed message for each way the route can end without a password. The
 * failed screen also offers sign-in (`SIGN_IN_PATH`): the recovery route for
 * someone who already set a password and reached this page again — by
 * Back, Forward, a reload or a second click on a spent link.
 */
export function messageForFailure(reason: FailureReason): string {
  switch (reason) {
    case "link_expired":
      return "This invitation link has expired or has already been used. If you have already set your password, sign in. Otherwise ask LINNFOSS for a new invitation.";
    case "token_missing":
      return "This page opens only from the link in your invitation email. If you have already set your password, sign in. Otherwise ask LINNFOSS for a new invitation.";
    case "link_invalid":
    case "no_session":
      return "This invitation link is invalid or could not be confirmed. Ask LINNFOSS for a new one.";
    case "fragment_refused":
      return "This link is in a form the console no longer accepts. Ask LINNFOSS for a new invitation.";
    case "type_unsupported":
      return "This link is not an invitation. Ask LINNFOSS for a new one.";
  }
}

/** Where the failed screen's "Go to sign-in" leads: the session gate. */
export const SIGN_IN_PATH = "/";

/**
 * What to put in the address bar in place of the landing URL: the route's
 * path, nothing else. No query (that is where the single-use token_hash
 * arrives, and an error redirect can put error_description there), no
 * fragment (where the refused implicit flow put a session). The input is
 * accepted only so that a test can prove the output does not depend on it.
 */
export function scrubTarget(_href: string): string {
  return ACCEPT_PATH;
}

/** Whether a pathname is the invite callback (a trailing slash is tolerated). */
export function isAcceptRoute(pathname: string): boolean {
  return pathname === ACCEPT_PATH || pathname === `${ACCEPT_PATH}/`;
}
